export type UserRole = 'user' | 'admin' | 'birthday_person';

export type UserProfile = {
  id: number;
  nickname: string;
  role: UserRole;
  roleLabel?: string;
  roleBadgeColor?: string;
  avatar: string | null;
  about: string | null;
  createdAt: string;
};

export type RoleDefinition = {
  key: string;
  label: string;
  badgeColor: string;
  sortOrder: number;
};

export type MemberUser = {
  id: number;
  nickname: string;
  role: string;
  roleLabel: string;
  roleBadgeColor: string;
  avatar: string | null;
  about: string | null;
  avatarLikesCount: number;
};

export type MemberProfile = MemberUser & {
  likedByMe: boolean;
  createdAt: string;
};

export type AuthResponse = {
  token: string;
  user: UserProfile;
};

export type PlatformEvent = {
  id: number;
  title: string;
  description: string | null;
  eventDate: string;
  emoji: string;
  createdBy: number;
  createdAt: string;
};

export type WallAuthor = {
  nickname: string;
  avatar: string | null;
};

export type WallPostCore = {
  id: number;
  message: string | null;
  drawing: string | null;
  createdAt: string;
  userId: number;
  wallUserId?: number | null;
  reactions: { emoji: string; count: number }[];
};

export type WallPost = WallPostCore & WallAuthor;

export type WallPostsResponse = {
  posts: WallPostCore[];
  authors: Record<string, WallAuthor>;
};

export type ProfileWallPost = {
  id: number;
  message: string | null;
  drawing: string | null;
  createdAt: string;
  userId: number;
  wallUserId: number | null;
  nickname: string;
  avatar: string | null;
};

export type VirtualGift = {
  id: number;
  giftType: string;
  message: string | null;
  createdAt: string;
  fromUserId: number | null;
  toUserId: number | null;
  nickname: string | null;
  avatar: string | null;
  toNickname: string | null;
  isPublic: boolean;
  isAnonymous: boolean;
  showMessageOnHover: boolean;
};

export type PlatformStats = {
  postsCount: number;
  membersCount: number;
  giftsCount: number;
  nextEvent: PlatformEvent | null;
};

export const GIFT_META: Record<string, { label: string; emoji: string; color: string }> = {
  rose: { label: 'Роза', emoji: '🌹', color: '#fb7185' },
  cake: { label: 'Торт', emoji: '🎂', color: '#f472b6' },
  heart: { label: 'Сердечко', emoji: '💗', color: '#ec4899' },
  star: { label: 'Звезда', emoji: '⭐', color: '#fbbf24' },
  gift: { label: 'Подарок', emoji: '🎁', color: '#a855f7' },
};

export const REACTION_EMOJIS = ['💗', '🎉', '🌸', '😍', '👏'];

export const CHAT_EMOJIS = ['😀', '😍', '🎉', '💗', '🌸', '👏', '🎂', '🥳', '✨', '😊', '🫶', '💖', '🌹', '💐'];

export type PlaygroundCategory = 'money' | 'health' | 'love';

export type PlaygroundScores = {
  money: number;
  health: number;
  love: number;
  total: number;
};

export type PlaygroundLeader = PlaygroundScores & {
  rank: number;
  userId: number;
  nickname: string;
  avatar: string | null;
};

export type PlaygroundUserScores = PlaygroundScores & {
  userId: number;
  nickname: string;
  avatar: string | null;
};

export const PLAYGROUND_CATEGORIES: Record<
  PlaygroundCategory,
  { label: string; emoji: string; color: string; gradient: string }
> = {
  money: { label: 'Деньги', emoji: '💰', color: '#d97706', gradient: 'linear-gradient(135deg, #fef3c7, #fde68a)' },
  health: { label: 'Здоровье', emoji: '💚', color: '#16a34a', gradient: 'linear-gradient(135deg, #dcfce7, #bbf7d0)' },
  love: { label: 'Любовь', emoji: '❤️', color: '#db2777', gradient: 'linear-gradient(135deg, #fce7f3, #fbcfe8)' },
};

export type QuestPartyMember = {
  userId: number;
  nickname: string;
  userRole: string;
  playerClass: string | null;
  hp: number;
  gold: number;
  love: number;
  maxHp: number;
  maxGold: number;
  maxLove: number;
  weaponId: string | null;
  fightClicks: number;
  fightDamage: number;
  fightTurnSubmitted?: boolean;
};

export type QuestSceneView = {
  sceneIndex: number;
  title: string;
  narrative: string;
  hpCost: number;
  goldCost: number;
  loveCost: number;
  effectiveHpCost: number;
  effectiveGoldCost: number;
  effectiveLoveCost: number;
  sceneType: string;
  hpPooled: number;
  goldPooled: number;
  lovePooled: number;
  cleared: boolean;
};

export type QuestShopWeapon = {
  id: string;
  name: string;
  description: string;
  goldCost: number;
  emoji: string;
};

export type QuestBossFight = {
  status: 'need_queue' | 'fighting' | 'waiting';
  turnOrder: { userId: number; nickname: string; weaponId: string | null }[];
  currentFighterUserId: number | null;
  currentFighterNickname: string | null;
  fightStartedAt: string | null;
  fightDurationSec: number;
  fightRemainingMs: number | null;
  canAttack: boolean;
  canSubmitDamage: boolean;
  myTurnStartedAt: string | null;
  /** @deprecated use canAttack */
  isMyTurn: boolean;
  canSetQueue: boolean;
  myClicksThisTurn: number;
  myDamageThisTurn: number;
  myTurnSubmitted: boolean;
  myQueuePosition: number | null;
  fightActive: boolean;
};

export type QuestState = {
  active: boolean;
  status: 'idle' | 'active' | 'won' | 'failed';
  runId: number | null;
  currentSceneIndex: number | null;
  startedAt: string | null;
  endsAt: string | null;
  bossHp: number | null;
  bossMaxHp: number | null;
  finalNote: string | null;
  scene: QuestSceneView | null;
  party: QuestPartyMember[];
  myMember: QuestPartyMember | null;
  log: { id: number; message: string; createdAt: string }[];
  shopWeapons: QuestShopWeapon[];
  bossFight: QuestBossFight | null;
};

export type QuestSceneConfig = {
  id: number;
  sceneIndex: number;
  title: string;
  narrative: string;
  hpCost: number;
  goldCost: number;
  loveCost: number;
  sceneType: string;
  riddleQuestion: string | null;
  riddleAnswers: string | null;
  choicesConfig: string | null;
  diceMax: number;
  diceThreshold: number;
};

export type BonusLootTier = {
  id: number;
  rewardAmount: number;
  chancePercent: number;
  sortOrder: number;
};

export type BonusLootTierInput = {
  rewardAmount: number;
  chancePercent: number;
};

export type QuestAdminConfig = {
  id: number;
  finalNote: string;
  durationMinutes: number;
  tickMinutes: number;
  bossMaxHp: number;
  bossPhase2Hp: number;
  weaponDoubleGoldCost: number;
  weaponCritGoldCost: number;
  weaponTimeGoldCost: number;
};

export type EditableQuestScene = QuestSceneConfig & {
  riddleAnswersText: string;
};

export const QUEST_CLASS_LABELS: Record<string, string> = {
  guard: '🛡️ Страж Рубежа',
  ranger: '🏹 Следопыт Туманных Троп',
  bard: '🎵 Бард Плетёных Песен',
  mage: '🔮 Маг Трёх Искр',
};
